import React, { useState, Component } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SuiClientProvider, WalletProvider } from '@mysten/dapp-kit';
import '@mysten/dapp-kit/dist/index.css';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import Strategies from './pages/Strategies';
import Positions from './pages/Positions';
import PairTrades from './pages/PairTrades';
import Performance from './pages/Performance';
import Markets from './pages/Markets';
import DeepBlue from './pages/DeepBlue';
import KuCoinFunding from './pages/KuCoinFunding';
import BinanceArb from './pages/BinanceArb';
import DecibelTrade from './pages/DecibelTrade';
import ApexBot from './pages/ApexBot';
import NaviLiquidator from './pages/NaviLiquidator';
import SQLiteHistory from './pages/SQLiteHistory';
import DydxBf   from './pages/DydxBf';
import DydxRay  from './pages/DydxRay';
import DydxApex from './pages/DydxApex';
import VeraBot     from './pages/VeraBot';
import FundingArb  from './pages/FundingArb';
import LpTrax      from './pages/LpTrax';
import Budget      from './pages/Budget';
import NaviTrax    from './pages/NaviTrax';
import SuilendTrax from './pages/SuilendTrax';
import { LayoutDashboard, Bot, LineChart, BarChart2, ArrowLeftRight, Sun, Moon, Bell, BellOff, LogOut, Zap, Percent, Building2, Database, Wallet, TrendingUp, Terminal, Waves, CalendarDays } from 'lucide-react';
import { WSProvider, useWS } from './WSContext';
import LoginOverlay from './components/LoginOverlay';
import BotSelectOverlay from './components/BotSelectOverlay';
import { ToastProvider, useToast } from './components/Toast';
import { NotificationSettingsPopup } from './components/NotificationSettingsPopup';
import OptionsWidget from './components/OptionsWidget';
import { ACTIVE_BOTS, PENDING_BOT_KEY } from './activeBots';

const queryClient   = new QueryClient();
const suiNetworks   = { mainnet: { url: 'https://fullnode.mainnet.sui.io' } } as const;

class ErrorBoundary extends Component<{ children: React.ReactNode }, { error: Error | null }> {
  constructor(props: any) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, fontFamily: 'monospace', background: '#061530', color: '#4A9FE8', minHeight: '100vh' }}>
          <h2 style={{ color: '#4A9FE8' }}>Runtime Error</h2>
          <pre style={{ whiteSpace: 'pre-wrap', color: '#e0c070' }}>{this.state.error.message}</pre>
          <pre style={{ whiteSpace: 'pre-wrap', color: '#888', fontSize: 12 }}>{this.state.error.stack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Lakers Logo ─────────────────────────────────────────────────────────────
function LakersLogo({ size = 44 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Top flame tip */}
      <polygon points="60,4 50,28 70,28" fill="#4A9FE8"/>
      {/* Side flame left */}
      <polygon points="26,18 38,42 52,30" fill="#4A9FE8" opacity="0.8"/>
      {/* Side flame right */}
      <polygon points="94,18 82,42 68,30" fill="#4A9FE8" opacity="0.8"/>
      {/* Ball body */}
      <circle cx="60" cy="72" r="42" fill="#003087"/>
      {/* Ball seam lines */}
      <path d="M 20 60 Q 60 44 100 60" stroke="#4A9FE8" strokeWidth="2.5" fill="none" opacity="0.6"/>
      <path d="M 20 80 Q 60 96 100 80" stroke="#4A9FE8" strokeWidth="2.5" fill="none" opacity="0.6"/>
      <path d="M 60 30 Q 44 60 60 114" stroke="#4A9FE8" strokeWidth="2.5" fill="none" opacity="0.6"/>
      <path d="M 60 30 Q 76 60 60 114" stroke="#4A9FE8" strokeWidth="2.5" fill="none" opacity="0.6"/>
      {/* Letter R highlight */}
      <circle cx="60" cy="72" r="18" fill="#4A9FE8" opacity="0.15"/>
    </svg>
  );
}

export type Theme = {
  bg: string; sidebar: string; border: string;
  text: string; subtext: string; muted: string;
  card: string; cardBorder: string;
  navActive: string; navHover: string;
  tableRow: string; tableHeader: string;
  positive: string; negative: string;
  btnPrimary: string; btnSecondary: string;
  inp: string; dark: boolean;
  gold: string;
};

export function getTheme(dark: boolean): Theme {
  return dark ? {
    dark: true,
    bg:           'bg-[#030d1f]',
    sidebar:      'bg-[#061530]',
    border:       'border-[#0a2560]',
    text:         'text-white',
    subtext:      'text-blue-100',
    muted:        'text-blue-400',
    card:         'bg-[#061530]/80',
    cardBorder:   'border-[#0a2560]',
    navActive:    'bg-[#0057A8]/30 text-white',
    navHover:     'text-blue-300 hover:bg-white/5 hover:text-white',
    tableRow:     'border-[#0a2560]/60 hover:bg-[#0a2560]/40',
    tableHeader:  'text-[#4A9FE8] border-[#0a2560]',
    positive:     'text-emerald-400',
    negative:     'text-rose-400',
    btnPrimary:   'bg-[#003087] hover:bg-[#0057A8] text-white',
    btnSecondary: 'bg-[#0a2560] hover:bg-[#0d3080] text-blue-200 border border-[#003087]',
    inp:          'bg-[#0a2560]/60 border-[#003087] text-white placeholder-blue-600 focus:border-[#4A9FE8]',
    gold:         'text-[#4A9FE8]',
  } : {
    dark: false,
    bg:           'bg-[#F0F4F8]',
    sidebar:      'bg-[#003087]',
    border:       'border-[#C5D3E8]',
    text:         'text-[#001A4D]',
    subtext:      'text-[#1A3A6B]',
    muted:        'text-[#5A6E8C]',
    card:         'bg-white',
    cardBorder:   'border-[#C5D3E8]',
    navActive:    'bg-white/15 text-white',
    navHover:     'text-blue-200 hover:bg-white/10 hover:text-white',
    tableRow:     'border-[#C5D3E8] hover:bg-[#E8EEF7]',
    tableHeader:  'text-[#003087] border-[#C5D3E8]',
    positive:     'text-[#007A33]',
    negative:     'text-[#C8102E]',
    btnPrimary:   'bg-[#003087] hover:bg-[#0057A8] text-white',
    btnSecondary: 'bg-white hover:bg-[#E8EEF7] text-[#003087] border border-[#C5D3E8]',
    inp:          'bg-white border-[#C5D3E8] text-[#001A4D] placeholder-[#5A6E8C] focus:border-[#003087]',
    gold:         'text-[#003087]',
  };
}

const SESSION_KEY = 'rt_user';

// Eski tek-key token sisteminden yeni per-endpoint sisteme geçiş (tek seferlik)
if (sessionStorage.getItem('rt_token')) {
  sessionStorage.removeItem('rt_token');
}

function AppInner() {
  // ÜÇ TEMA: koyu (varsayılan) → terminal (Bloomberg esinli) → açık.
  // `dark` bilinçli olarak türetiliyor: 25 bileşen `dark: boolean` bekliyor ve
  // terminal de koyu bir tema. Böylece hiçbir bileşen imzası değişmiyor; terminal
  // görünümünü CSS katmanı (terminal.css) `data-theme` üzerinden kuruyor.
  type ThemeMode = 'dark' | 'terminal' | 'light';
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    const v = (typeof localStorage !== 'undefined' && localStorage.getItem('bq_theme')) as ThemeMode | null;
    return v === 'dark' || v === 'terminal' || v === 'light' ? v : 'dark';
  });
  const dark = themeMode !== 'light';
  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeMode);
    try { localStorage.setItem('bq_theme', themeMode); } catch { /* özel sekme */ }
  }, [themeMode]);
  const THEME_ORDER: ThemeMode[] = ['dark', 'terminal', 'light'];
  const nextTheme = () => THEME_ORDER[(THEME_ORDER.indexOf(themeMode) + 1) % THEME_ORDER.length];
  const THEME_LABEL: Record<ThemeMode, string> = {
    dark: 'Koyu Tema', terminal: 'Terminal Teması', light: 'Açık Tema',
  };
  const [notificationsOn, setNotificationsOn] = useState(true);
  const [notifPopupOpen, setNotifPopupOpen] = useState(false);
  const [authUser, setAuthUser]           = useState<string | null>(() => sessionStorage.getItem(SESSION_KEY));
  const [botChosen, setBotChosen]         = useState(false);
  const { connected, sendWs, subscribe, botList, activeBotId, setActiveBotId, switchEndpoint, clearCurrentToken } = useWS();
  const t = getTheme(dark);
  const isDeepBlue      = activeBotId === 'deepblue-main';
  const isDydx          = activeBotId === 'dydx-main';
  const isDydxBf        = activeBotId === 'dydx-bf';
  const isDydxRay       = activeBotId === 'dydx-ray';
  const isDydxApex      = activeBotId === 'dydx-apex';
  const isBinanceArb    = activeBotId === 'binance-arb';
  const isKuCoinFunding = activeBotId === 'kucoin-funding';
  const isDecibelTrade  = activeBotId === 'decibel-trade';
  const isApexBot       = activeBotId === 'apex-bot';
  const isNaviLiq       = activeBotId === 'navi-liquidator';
  const isVera          = activeBotId === 'vera' || activeBotId === 'Vera Wallet';
  const isCex           = isBinanceArb || isKuCoinFunding || isDecibelTrade || isNaviLiq || isApexBot;

  // Bağlantı kurulunca mevcut durumu sorgula
  React.useEffect(() => {
    if (!connected) return;
    sendWs({ type: 'get_notifications' });
  }, [connected, sendWs]);

  // Token auth başarısız → sunucu restart olmuş, token geçersiz.
  // Kullanıcı "giriş yapmış" görünüyor ama veri gelmiyor. Zorla re-login.
  React.useEffect(() => {
    return subscribe((data: any) => {
      if (data.type === 'auth_result' && data.payload?.success === false && authUser) {
        // Sadece mevcut endpoint'in token'ını sil (clearCurrentToken),
        // diğer endpoint'lerin geçerli token'ları korunur.
        clearCurrentToken();
        sessionStorage.removeItem(SESSION_KEY);
        setAuthUser(null);
        setBotChosen(false);
      }
    });
  }, [subscribe, authUser, clearCurrentToken]);

  // Backend'den gelen bildirim durumunu dinle
  React.useEffect(() => {
    return subscribe((data: any) => {
      if (data.type === 'notifications_state') {
        setNotificationsOn(data.payload.enabled);
      }
    });
  }, [subscribe]);

  const toggleNotifications = () => {
    const next = !notificationsOn;
    setNotificationsOn(next);
    sendWs({ type: 'set_notifications', payload: { enabled: next } });
  };

  const handleLogin = (username: string) => {
    sessionStorage.setItem(SESSION_KEY, username);
    setAuthUser(username);
    setBotChosen(false); // her girişte bot seçim ekranı açılsın
  };

  const BLUEFIN_WS = window.location.port === '3000' || window.location.port === '3001'
    ? `ws://${window.location.hostname}:8866`
    : 'wss://ws.barbarosquant.org/ws';
  const ORDERLY_WS = window.location.port === '3000' || window.location.port === '3001'
    ? `ws://${window.location.hostname}:8876`
    : 'wss://ws.barbarosquant.org/sol-ws';
  const DEEPBLUE_WS = window.location.port === '3000' || window.location.port === '3001'
    ? `ws://${window.location.hostname}:8879`
    : 'wss://ws.barbarosquant.org/deepblue-ws';
  const DYDX_WS = window.location.port === '3000' || window.location.port === '3001'
    ? `ws://${window.location.hostname}:8887`
    : 'wss://ws.barbarosquant.org/dydx-ws';
  const ORDERLY_ID      = 'orderly-main';
  const ORDERLY_NAME    = 'Timur Bey';
  const DEEPBLUE_ID     = 'deepblue-main';
  const DEEPBLUE_NAME   = 'DeepBlue';
  // hyperliquid-main KALDIRILDI (02/09/2026). Hyperliquid artık ana backend'in
  // bots.json'ında 'Hyperliquid' anahtarıyla duruyor ve listeye oradan geliyor;
  // ikisi de AYNI hl-node köprüsüne (8868), yani aynı cüzdana bakıyordu. Eski
  // giriş funding arb, grup ve SQLite geçmişi yüzeylerinin hiçbirine sahip
  // değildi. Timur Bey'de de aynı ikilik aynı şekilde temizlenmişti.

  // Bluefin bot listesini önbellekte tut — Orderly'deyken de göstermek için
  const [cachedBluefinBots, setCachedBluefinBots] = React.useState<{id:string;name:string}[]>(() => {
    try { return JSON.parse(localStorage.getItem('raydium_botlist') || '[]'); }
    catch { return []; }
  });

  const DYDX_ID           = 'dydx-main';
  const DYDX_NAME         = 'dYdX Perps';
  const DYDXBF_ID         = 'dydx-bf';
  const DYDXBF_NAME       = 'dYdX × Bluefin';
  const DYDXRAY_ID        = 'dydx-ray';
  const DYDXRAY_NAME      = 'dYdX × Raydium';
  const DYDXAPEX_ID       = 'dydx-apex';
  const DYDXAPEX_NAME     = 'dYdX × Apex';
  const BINANCE_ARB_ID    = 'binance-arb';
  const KUCOIN_FUNDING_ID = 'kucoin-funding';
  const DECIBEL_TRADE_ID  = 'decibel-trade';
  const APEX_BOT_ID       = 'apex-bot';
  const NAVI_LIQ_ID       = 'navi-liquidator';
  const isStaticBot = (id: string) => id === DEEPBLUE_ID || id === DYDX_ID || id === DYDXBF_ID || id === DYDXRAY_ID || id === DYDXAPEX_ID || id === BINANCE_ARB_ID || id === KUCOIN_FUNDING_ID || id === DECIBEL_TRADE_ID || id === APEX_BOT_ID || id === NAVI_LIQ_ID;

  React.useEffect(() => {
    if (botList.length > 0 && !isStaticBot(activeBotId)) {
      setCachedBluefinBots(botList);
      localStorage.setItem('raydium_botlist', JSON.stringify(botList));
    }
  }, [botList, activeBotId]);

  // Dropdown'da her zaman tüm botlar görünsün (CEX statik botlar hariç)
  // ORDERLY_ID (Timur Bey / sol-ws) artık bot3 olarak MAIN_WS'e taşındı — listeden kaldırıldı
  // ÜST ŞERİTTE yalnız aktif kullanılan botlar. Diğerleri silinmedi; yalnız bot
  // seçim ekranındaki "Diğerleri" kartından erişiliyor (BotSelectOverlay). Şerit
  // 18 sekmeye çıkmıştı ve kullanılanları bulmak zorlaşmıştı.
  // Not: O AN AÇIK olan bot listede olmasa bile şeritte kalır — aksi halde
  // "Diğerleri"nden bir sayfa açıldığında sekmesi kaybolur ve geri dönülemez.
  // Liste `src/activeBots.ts`te: /lptrax sağ rayı da aynı kaynağı basıyor.
  const PRIMARY_BOT_IDS = ACTIVE_BOTS.map(b => b.id);
  const allBotsRaw = [
    ...(isStaticBot(activeBotId) ? cachedBluefinBots : botList),
    { id: DEEPBLUE_ID,    name: DEEPBLUE_NAME,    network: 'sui'    },
    { id: DYDX_ID,        name: DYDX_NAME,        network: 'cosmos'  },
    { id: DYDXBF_ID,      name: DYDXBF_NAME,      network: 'arb'     },
    { id: DYDXRAY_ID,     name: DYDXRAY_NAME,     network: 'arb'     },
    { id: DYDXAPEX_ID,    name: DYDXAPEX_NAME,    network: 'arb'     },
    { id: NAVI_LIQ_ID,    name: 'Navi Liquidator', network: 'sui-liq' },
  ];
  const allBots = allBotsRaw.filter(
    (b: any) => PRIMARY_BOT_IDS.includes(b.id) || b.id === activeBotId);

  const handleBotDropdownChange = (id: string) => {
    if (id === ORDERLY_ID) {
      switchEndpoint(ORDERLY_WS);
      setActiveBotId(ORDERLY_ID);
    } else if (id === DEEPBLUE_ID) {
      switchEndpoint(DEEPBLUE_WS);
      setActiveBotId(DEEPBLUE_ID);
    } else if (id === DYDX_ID) {
      switchEndpoint(DYDX_WS);
      setActiveBotId(DYDX_ID);
    } else if (id === DYDXBF_ID) {
      setActiveBotId(DYDXBF_ID);
    } else if (id === DYDXRAY_ID) {
      setActiveBotId(DYDXRAY_ID);
    } else if (id === DYDXAPEX_ID) {
      setActiveBotId(DYDXAPEX_ID);
    } else if (id === BINANCE_ARB_ID) {
      setActiveBotId(BINANCE_ARB_ID);
    } else if (id === KUCOIN_FUNDING_ID) {
      setActiveBotId(KUCOIN_FUNDING_ID);
    } else if (id === DECIBEL_TRADE_ID) {
      setActiveBotId(DECIBEL_TRADE_ID);
    } else if (id === APEX_BOT_ID) {
      setActiveBotId(APEX_BOT_ID);
    } else if (id === NAVI_LIQ_ID) {
      setActiveBotId(NAVI_LIQ_ID);
    } else {
      if (isStaticBot(activeBotId)) switchEndpoint(BLUEFIN_WS);
      setActiveBotId(id);
    }
  };

  // /lptrax sağ rayından bir bota basıldığında LP Trax `/`'ye yönlendirip bot
  // id'sini sessionStorage'a bırakıyor (ayrı kök olduğu için state paylaşamıyor).
  // Burada bir kez okunup SİLİNİYOR → bot seçim ekranı atlanır, doğrudan o bot
  // açılır. Yol `handleBotDropdownChange` ile aynı: üst şeritteki geçişin ta kendisi.
  const devirAlindi = React.useRef(false);
  React.useEffect(() => {
    if (devirAlindi.current || !authUser) return;
    let devir: string | null = null;
    try { devir = sessionStorage.getItem(PENDING_BOT_KEY); } catch { /* özel sekme */ }
    if (!devir) return;
    devirAlindi.current = true;
    try { sessionStorage.removeItem(PENDING_BOT_KEY); } catch { /* özel sekme */ }
    handleBotDropdownChange(devir);
    setBotChosen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  const handleBotSelect = (botId: string, wsEndpoint?: string) => {
    if (botId === BINANCE_ARB_ID) {
      setActiveBotId(BINANCE_ARB_ID);
    } else if (botId === KUCOIN_FUNDING_ID) {
      setActiveBotId(KUCOIN_FUNDING_ID);
    } else if (botId === DECIBEL_TRADE_ID) {
      setActiveBotId(DECIBEL_TRADE_ID);
    } else if (botId === APEX_BOT_ID) {
      setActiveBotId(APEX_BOT_ID);
    } else if (botId === NAVI_LIQ_ID) {
      setActiveBotId(NAVI_LIQ_ID);
    } else if (botId === DYDX_ID) {
      switchEndpoint(DYDX_WS);
      setActiveBotId(DYDX_ID);
    } else if (botId === DYDXBF_ID) {
      setActiveBotId(DYDXBF_ID);
    } else if (botId === DYDXRAY_ID) {
      setActiveBotId(DYDXRAY_ID);
    } else if (botId === DYDXAPEX_ID) {
      setActiveBotId(DYDXAPEX_ID);
    } else if (wsEndpoint) {
      switchEndpoint(wsEndpoint);
      setActiveBotId(botId);
    } else if (botId === DEEPBLUE_ID) {
      switchEndpoint(DEEPBLUE_WS);
      setActiveBotId(botId);
    } else {
      setActiveBotId(botId);
    }
    setBotChosen(true);
  };

  const logout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem('rt_token');   // Oturum token'ını da temizle
    setAuthUser(null);
    setBotChosen(false);
  };

  if (!authUser) {
    return <LoginOverlay onSuccess={handleLogin} />;
  }

  // /lptrax — login gerekli ama bot-seçim akışını atla (standalone LP tracker)
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/lptrax')) {
    return (
      <div className={`h-screen overflow-auto ${t.bg} ${t.text}`}>
        <LpTrax dark={dark} />
      </div>
    );
  }

  // /butce — kişisel bütçe defteri (standalone, bot-seçim akışı yok)
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/butce')) {
    return (
      <div className={`h-screen overflow-auto ${t.bg} ${t.text}`}>
        <Budget dark={dark} />
      </div>
    );
  }

  // /navi — login gerekli ama bot-seçim akışını atla (standalone Navi takip)
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/navi')) {
    return (
      <div className={`h-screen overflow-auto ${t.bg} ${t.text}`}>
        <NaviTrax dark={dark} />
      </div>
    );
  }

  // /suilend — login gerekli ama bot-seçim akışını atla (standalone Suilend takip)
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/suilend')) {
    return (
      <div className={`h-screen overflow-auto ${t.bg} ${t.text}`}>
        <SuilendTrax dark={dark} />
      </div>
    );
  }

  if (!botChosen) {
    return <BotSelectOverlay username={authUser} onSelect={handleBotSelect} />;
  }

  return (
    <Router>
      <div className={`flex h-screen ${t.bg} ${t.text}`}>
        {/* Sidebar */}
        <div className={`w-56 ${isCex ? (dark ? 'bg-[#0e0a00]' : 'bg-[#1a1200]') : t.sidebar} border-r ${isCex ? (dark ? 'border-[#2a1e00]' : 'border-[#3a2e00]') : t.border} flex flex-col`}>
          <button
            onClick={() => setBotChosen(false)}
            className={`w-full p-4 border-b ${isCex ? (dark ? 'border-[#2a1e00]' : 'border-[#3a2e00]') : dark ? 'border-[#0a2560]' : 'border-[#0057A8]'} flex items-center gap-3 text-left transition-opacity hover:opacity-75 active:opacity-50`}
            title="Bot seçim ekranına dön"
          >
            <div className="flex-shrink-0"><LakersLogo size={44} /></div>
            <div>
              <h1 className="text-base font-black tracking-widest text-white uppercase">Raydium</h1>
              <p className={`text-xs opacity-80 ${isCex ? 'text-yellow-600' : 'text-blue-200'}`}>
                {isBinanceArb ? 'CEX Arb' : isKuCoinFunding ? 'KuCoin Funding' : isDecibelTrade ? 'Decibel Trade' : isApexBot ? 'Apex Pro' : isNaviLiq ? 'Navi Liquidator' : isVera ? 'Vera Wallet' : isDydx ? 'dYdX Perps' : isDydxBf ? 'dYdX × BF Arb' : isDydxRay ? 'dYdX × Ray Arb' : isDydxApex ? 'dYdX × Apex Arb' : 'Perps Bot'}
              </p>
            </div>
          </button>

          <nav className="flex-1 p-3 space-y-1">
            {(isBinanceArb ? [
              { to: '/binance-arb', label: 'Funding Arb',    icon: <Building2 size={16} />, end: true },
              { to: '/performance', label: 'Performans',      icon: <BarChart2 size={16} /> },
            ] : isDydxBf ? [
              { to: '/dydx-bf',   label: 'dYdX × BF',   icon: <ArrowLeftRight size={16} />, end: true },
            ] : isDydxRay ? [
              { to: '/dydx-ray',  label: 'dYdX × Ray',  icon: <ArrowLeftRight size={16} />, end: true },
            ] : isDydxApex ? [
              { to: '/dydx-apex', label: 'dYdX × Apex', icon: <ArrowLeftRight size={16} />, end: true },
            ] : isDydx ? [
              { to: '/',            label: 'Dashboard',       icon: <LayoutDashboard size={16} />, end: true },
              { to: '/positions',   label: 'Pozisyonlar',     icon: <LineChart size={16} /> },
              { to: '/performance', label: 'Performans',      icon: <BarChart2 size={16} /> },
              { to: '/history',     label: 'İşlem Geçmişi',  icon: <Database size={16} /> },
            ] : isKuCoinFunding ? [
              { to: '/kucoin',      label: 'Funding Rates',   icon: <Percent size={16} />, end: true },
            ] : isDecibelTrade ? [
              { to: '/decibel',     label: 'Perps Trading',   icon: <Zap size={16} />, end: true },
            ] : isApexBot ? [
              { to: '/apex-bot',    label: 'Apex Perps',      icon: <Zap size={16} />, end: true },
            ] : isNaviLiq ? [
              { to: '/navi',        label: 'Likidasyon Sniper', icon: <Zap size={16} />, end: true },
            ] : isVera ? [
              { to: '/vera',        label: 'Vera Wallet',     icon: <Wallet size={16} />, end: true },
              { to: '/history',     label: 'İşlem Geçmişi',  icon: <Database size={16} /> },
            ] : [
              { to: '/',            label: 'Dashboard',       icon: <LayoutDashboard size={16} />, end: true },
              ...(!isDeepBlue ? [
                { to: '/strategies',  label: 'Stratejiler',   icon: <Bot size={16} /> },
                { to: '/positions',   label: 'Pozisyonlar',   icon: <LineChart size={16} /> },
                { to: '/pair-trades', label: 'Pair Trade',    icon: <ArrowLeftRight size={16} /> },
              ] : [
                { to: '/deepblue',    label: 'Arbitraj',      icon: <Zap size={16} /> },
                { to: '/positions',   label: 'Pozisyonlar',   icon: <LineChart size={16} /> },
              ]),
              { to: '/funding-arb', label: 'Funding Arb',    icon: <TrendingUp size={16} /> },
              { to: '/performance', label: 'Performans',      icon: <BarChart2 size={16} /> },
              { to: '/history',    label: 'İşlem Geçmişi',  icon: <Database size={16} /> },
            ]).map(item => (
              <NavLink key={item.to} to={item.to} end={(item as any).end}
                className={({ isActive }) => {
                  const active = isCex ? 'bg-[#F0B90B]/15 text-[#F0B90B]' : t.navActive;
                  const hover  = isCex ? 'text-yellow-600 hover:bg-[#F0B90B]/5 hover:text-[#F0B90B]' : t.navHover;
                  return `flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors font-medium ${isActive ? active : hover}`;
                }}>
                {item.icon}<span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className={`p-3 border-t ${dark ? 'border-[#0a2560]' : 'border-[#0057A8]'} space-y-2`}>
            <button onClick={() => setThemeMode(nextTheme())}
              title={`Sıradaki: ${THEME_LABEL[nextTheme()]}`}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors ${dark ? 'text-blue-400 hover:bg-[#0a2560]' : 'text-blue-200 hover:bg-[#0057A8]'}`}>
              {themeMode === 'dark' ? <Terminal size={14} />
                : themeMode === 'terminal' ? <Sun size={14} />
                : <Moon size={14} />}
              {THEME_LABEL[nextTheme()]}
            </button>
            <div className="relative">
              {notifPopupOpen && (
                <NotificationSettingsPopup
                  notificationsOn={notificationsOn}
                  onToggle={toggleNotifications}
                  dark={dark}
                  onClose={() => setNotifPopupOpen(false)}
                />
              )}
              <button onClick={() => setNotifPopupOpen(v => !v)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors ${
                  notificationsOn
                    ? dark ? 'text-[#4A9FE8] hover:bg-[#0a2560]' : 'text-[#4A9FE8] hover:bg-[#0057A8]'
                    : dark ? 'text-blue-600 hover:bg-[#0a2560]' : 'text-blue-300 hover:bg-[#0057A8]'
                }`}>
                {notificationsOn ? <Bell size={14} /> : <BellOff size={14} />}
                {notificationsOn ? 'Bildirimler Açık' : 'Bildirimler Kapalı'}
              </button>
            </div>
            <div className="flex items-center gap-2 px-3 py-1">
              <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-rose-500'}`} />
              <span className={`text-xs ${dark ? 'text-blue-400' : 'text-blue-200'}`}>
                {connected ? 'Bağlantı aktif' : 'Bağlanıyor...'}
              </span>
            </div>
            <button onClick={logout}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs transition-colors ${dark ? 'text-rose-400 hover:bg-[#0a2560]' : 'text-rose-400 hover:bg-[#0057A8]'}`}>
              <span className="flex items-center gap-2">
                <LogOut size={14} />
                Çıkış Yap
              </span>
              <span className={`text-[10px] ${dark ? 'text-blue-400' : 'text-blue-200'}`}>{authUser}</span>
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className={`flex-1 flex flex-col ${t.bg}`}>

          {/* Bot seçici üst bar — LP Trax düğmesi burada durduğu için bot listesi
              BOŞ olsa da (soğuk açılışta botList henüz gelmemiş olabilir) şerit
              basılır; eskiden tümüyle `allBots.length > 0`a bağlıydı. */}
          <div className={`shrink-0 flex items-center gap-1.5 px-5 py-2 border-b flex-wrap ${dark ? 'border-[#0a2560] bg-[#03112b]/60' : 'border-[#C5D3E8] bg-white/60'}`}>
            {allBots.map(bot => {
              const active = bot.id === activeBotId;
              const net = (bot as any).network;
              const prefix = net === 'sui' ? '⬡' : net === 'evm' ? '⬡' : net === 'sui-liq' ? '⚡' : net === 'cosmos' ? '✦' : net === 'arb' ? '⇄' : '◎';
              return (
                <button key={bot.id} onClick={() => handleBotDropdownChange(bot.id)}
                  className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium border transition-all ${
                    active
                      ? 'bg-[#4A9FE8]/20 border-[#4A9FE8]/60 text-[#4A9FE8]'
                      : dark
                        ? 'border-[#0a2560] text-[#4A9FE8]/50 hover:text-[#4A9FE8] hover:border-[#4A9FE8]/40'
                        : 'border-[#C5D3E8] text-[#003087]/50 hover:text-[#003087] hover:border-[#003087]/40'
                  }`}>
                  <span className="text-[10px]">{prefix}</span>
                  {bot.name}
                </button>
              );
            })}
            {/* LP Trax — şeridin sağındaki boşluğa. `ml-auto`: bot sekmeleri
                `flex-wrap` olduğu için sabit konum değil, son satırın sonuna
                oturur. LP Trax ayrı kök olduğundan tam sayfa yönlendirme. */}
            <button onClick={() => window.location.assign('/lptrax')}
              title="LP Trax — pozisyon bazlı LP takibi"
              className={`ml-auto flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium border transition-all ${
                dark
                  ? 'border-sky-500/40 text-sky-400 hover:bg-sky-500/15 hover:border-sky-500/70'
                  : 'border-[#0057A8]/40 text-[#0057A8] hover:bg-[#0057A8]/10 hover:border-[#0057A8]/70'
              }`}>
              <Waves size={12} /> LP Trax
            </button>
            <button onClick={() => window.location.assign('/butce')}
              title="Bütçe — kişisel gelir/gider defteri"
              className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium border transition-all ${
                dark
                  ? 'border-violet-500/40 text-violet-300 hover:bg-violet-500/15 hover:border-violet-500/70'
                  : 'border-violet-700/40 text-violet-700 hover:bg-violet-700/10 hover:border-violet-700/70'
              }`}>
              <CalendarDays size={12} /> Bütçe
            </button>
          </div>

          <div className="flex-1 overflow-auto">
          <Routes>
            <Route path="/"            element={
              isBinanceArb    ? <Navigate to="/binance-arb" replace /> :
              isKuCoinFunding ? <Navigate to="/kucoin"      replace /> :
              isDecibelTrade  ? <Navigate to="/decibel"     replace /> :
              isApexBot       ? <Navigate to="/apex-bot"    replace /> :
              isNaviLiq       ? <Navigate to="/navi"        replace /> :
              isVera          ? <Navigate to="/vera"        replace /> :
              isDeepBlue      ? <Navigate to="/deepblue"    replace /> :
              isDydxBf        ? <Navigate to="/dydx-bf"     replace /> :
              isDydxRay       ? <Navigate to="/dydx-ray"    replace /> :
              isDydxApex      ? <Navigate to="/dydx-apex"   replace /> :
              <Dashboard dark={dark} />
            } />
            <Route path="/markets"     element={<Markets       dark={dark} />} />
            <Route path="/strategies"  element={<Strategies    dark={dark} />} />
            <Route path="/positions"   element={<Positions     dark={dark} />} />
            <Route path="/pair-trades" element={<PairTrades    dark={dark} />} />
            <Route path="/deepblue"    element={<DeepBlue      dark={dark} />} />
            <Route path="/performance" element={<Performance   dark={dark} />} />
            <Route path="/kucoin"      element={<KuCoinFunding dark={dark} />} />
            <Route path="/binance-arb" element={<BinanceArb    dark={dark} />} />
            <Route path="/decibel"     element={<DecibelTrade  dark={dark} />} />
            <Route path="/apex-bot"    element={<ApexBot        dark={dark} />} />
            <Route path="/navi"        element={<NaviLiquidator dark={dark} />} />
            <Route path="/history"     element={<SQLiteHistory  dark={dark} />} />
            <Route path="/dydx-bf"    element={<DydxBf         dark={dark} />} />
            <Route path="/dydx-ray"   element={<DydxRay        dark={dark} />} />
            <Route path="/dydx-apex"  element={<DydxApex       dark={dark} />} />
            <Route path="/vera"       element={<VeraBot         dark={dark} />} />
            <Route path="/funding-arb" element={<FundingArb      dark={dark} />} />
          </Routes>
          </div>{/* /overflow-auto */}
        </div>{/* /flex-col */}
      </div>

      {/* Floating Options Pricer Widget */}
      <OptionsWidget dark={dark} />
    </Router>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SuiClientProvider networks={suiNetworks} defaultNetwork="mainnet">
        <WalletProvider autoConnect>
          <ErrorBoundary>
            <ToastProvider>
              <WSProvider>
                <AppInner />
              </WSProvider>
            </ToastProvider>
          </ErrorBoundary>
        </WalletProvider>
      </SuiClientProvider>
    </QueryClientProvider>
  );
}
